import moment from 'moment';
import * as R from 'ramda';

const isNullOrEmpty = R.either(R.isNil, R.isEmpty);

const keyExistsInObject = (object, key) => {
  if (isNullOrEmpty(object) || isNullOrEmpty(key)) {
    return false;
  }

  if (Object.prototype.hasOwnProperty.call(object, key)) {
    return !isNullOrEmpty(object[key]);
  }

  return false;
};

const formatDate = timestamp => moment(timestamp).format('DD/MM/YYYY HH:mm:ss');

const aclRoles = {
  READ: 1,
  WRITE: 2,
  ADMIN: 3,
};

const hasPermission = (userRole, requiredRole) => aclRoles[userRole] >= aclRoles[requiredRole];

const redirectKey = 'postLoginRedirect';
const oauthNonceKey = 'oauthStateNonce';

// Only relative in-app paths are accepted, so a crafted link cannot redirect
// elsewhere (backslashes are rejected because browsers normalize \ to /).
const isSafeRedirectPath = (path) =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.includes('\\') &&
  path !== '/';

// Storage access can throw (blocked cookies, sandboxed iframe); a lost value
// must never break the flow that touched it, so all access degrades silently.
const safeStorageGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeStorageSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    // ignore
  }
};

const safeStorageRemove = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    // ignore
  }
};

const saveRedirect = (fullPath) => {
  if (isSafeRedirectPath(fullPath)) {
    safeStorageSet(redirectKey, fullPath);
  }
};

const consumeRedirect = () => {
  const path = safeStorageGet(redirectKey);
  safeStorageRemove(redirectKey);
  return isSafeRedirectPath(path) ? path : '/dashboard';
};

// Anti-forgery state for the OAuth round trip: a random nonce validated on the
// callback, plus the intended post-login path. Carrying the path in the state
// binds it to this specific login attempt, so it wins over a possibly stale
// stored redirect when the callback returns.
const createOauthState = () => {
  const values = new Uint32Array(4);
  window.crypto.getRandomValues(values);
  const nonce = Array.from(values, value => value.toString(16).padStart(8, '0')).join('');

  safeStorageSet(oauthNonceKey, nonce);

  const state = { nonce };
  const redirect = safeStorageGet(redirectKey);
  if (isSafeRedirectPath(redirect)) {
    state.redirect = redirect;
  }

  return JSON.stringify(state);
};

const consumeOauthState = (state) => {
  const invalid = { valid: false, redirect: null };

  let parsed;
  try {
    parsed = JSON.parse(state);
  } catch (error) {
    return invalid;
  }

  const storedNonce = safeStorageGet(oauthNonceKey);
  safeStorageRemove(oauthNonceKey);

  if (!parsed || !storedNonce || parsed.nonce !== storedNonce) {
    return invalid;
  }

  return {
    valid: true,
    redirect: isSafeRedirectPath(parsed.redirect) ? parsed.redirect : null,
  };
};

export {
  formatDate,
  hasPermission,
  keyExistsInObject,
  isNullOrEmpty,
  saveRedirect,
  consumeRedirect,
  createOauthState,
  consumeOauthState,
};
